import React, { useEffect, useState } from "react";
import { Box, Flex, HStack, IconButton, Spinner, Text } from "@chakra-ui/react";
import { CaretLeft, CaretRight, Eye, FlagCheckered, PencilSimple } from "@phosphor-icons/react";
import { DataPreviewTable, formatCell } from "./DataPreviewTable";

// The Commit stage's left column: a read-only render of the data behind the Add/Replace decision on the
// right. The Preview and Commit tabs both render the whole commit stage (WorkingPanel's MappedStage), so
// this is what either tab shows. `plain` mode is the post-commit case: the "All data committed" banner
// instead of the legend/segmented control (brief §4: after a commit with nothing new, the Commit tab
// disables and the user lands back on this same chrome under "Preview").

const SCROLL_SX = {
  "&::-webkit-scrollbar": { width: "6px", height: "6px" },
  "&::-webkit-scrollbar-thumb": { bg: "transparent", borderRadius: "full" },
  "&:hover::-webkit-scrollbar-thumb": { bg: "gray.300" },
  scrollbarWidth: "thin",
  scrollbarColor: "transparent transparent",
  "&:hover": { scrollbarColor: "var(--chakra-colors-gray-300) transparent" },
};

/**
 * The left-hand Preview. `commitState` is a `useCommitState()` result; `plain` strips the legend and
 * segmented control for the post-commit, nothing-to-review case (brief §4).
 */
export function CommitPreview({ commitState, onGoToTransform, plain = false }) {
  const {
    outputRows,
    isLoadingOutputRows,
    mappedCount,
    hasExisting,
    existingRowCount,
    isLoadingExistingCount,
    isReplace,
    hasChangedSinceCommit,
    canClassifyRowChanges,
    lastCommit,
    readyPage,
    setReadyPage,
    existingRows,
    isLoadingExistingRows,
    existingPage,
    setExistingPage,
  } = commitState;

  const defaultView = plain ? "ready" : hasChangedSinceCommit ? "changes" : hasExisting ? "after" : "ready";
  const [view, setView] = useState(defaultView);

  // The default view depends on data that arrives after mount (hasExisting / hasChangedSinceCommit come
  // off queries) — reset it whenever the computed default actually changes, not on every render.
  useEffect(() => {
    setView(defaultView);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plain, hasChangedSinceCommit, hasExisting]);

  const columns = outputRows?.columns ?? [];
  const totalSessionRows = outputRows?.total_rows ?? mappedCount;
  const rows = outputRows?.rows ?? [];
  const readyTotalPages = outputRows?.total_pages ?? 1;
  // Already re-keyed onto the frame's columns (useCommitState → alignExistingRows), so the existing table
  // reads with the same headers as the ready one.
  const existingColumns = existingRows?.columns ?? columns;
  const existing = existingRows?.rows ?? [];
  const existingTotalPages = existingRows?.total_pages ?? 1;
  // The After view puts both lists under ONE header row, so it needs one column set. The existing rows'
  // set is the frame's columns plus any field the frame lacks, i.e. a superset — use it for both, padding
  // the mapped rows with blanks for the extra fields.
  const afterColumns = existingColumns.length >= columns.length ? existingColumns : columns;
  const padTo = (row, width) => (row.length >= width ? row : [...row, ...Array(width - row.length).fill(null)]);

  const views = plain
    ? null
    : hasChangedSinceCommit
    ? [
        { key: "changes", label: `Changes${canClassifyRowChanges ? ` (${commitState.changes ?? 0})` : ""}` },
        { key: "allSession", label: `All session rows (${totalSessionRows.toLocaleString()})` },
      ]
    : hasExisting
    ? [
        { key: "ready", label: `Ready to commit (${mappedCount.toLocaleString()})` },
        { key: "existing", label: `Existing (${(existingRowCount ?? 0).toLocaleString()})` },
        { key: "after", label: `After commit (${(isReplace ? mappedCount : (existingRowCount ?? 0) + mappedCount).toLocaleString()})` },
      ]
    : null;

  return (
    <Flex direction="column" flex="1" minH={0} minW={0} pr={6} py={3} gap={3}>
      <PreviewHeader
        plain={plain}
        hasExisting={hasExisting}
        hasChangedSinceCommit={hasChangedSinceCommit}
        isReplace={isReplace}
        mappedCount={mappedCount}
        view={view}
        setView={setView}
        views={views}
      />

      {plain && lastCommit && <AllCommittedBanner lastCommit={lastCommit} mappedCount={mappedCount} />}

      <Box
        flex="1"
        minH={0}
        minW={0}
        position="relative"
        border="1px solid"
        borderColor="gray.200"
        borderRadius="10px"
        overflow="hidden"
        bg="white"
      >
        {isLoadingOutputRows && !outputRows ? (
          <Flex align="center" justify="center" h="100%" color="gray.500" gap={2}>
            <Spinner size="sm" />
            <Text fontSize="13px">Loading the data…</Text>
          </Flex>
        ) : view === "changes" && !canClassifyRowChanges ? (
          <ChangesUnavailableNotice />
        ) : view === "ready" || view === "allSession" || plain ? (
          <Flex direction="column" h="100%">
            <Box flex="1" minH={0} overflow="auto" sx={SCROLL_SX}>
              <DataPreviewTable columns={columns} rows={rows} />
            </Box>
            <PreviewPager page={readyPage} totalPages={readyTotalPages} onPage={setReadyPage} />
          </Flex>
        ) : view === "existing" ? (
          isLoadingExistingRows && !existingRows ? (
            <LoadingNotice label="Loading the task's existing rows…" />
          ) : existing.length === 0 ? (
            <EmptyExisting isLoading={isLoadingExistingCount} />
          ) : (
            <Flex direction="column" h="100%">
              {isReplace && (
                <BandHeader
                  tone="removed"
                  label={`These ${(existingRowCount ?? 0).toLocaleString()} rows will be deleted by Replace all rows`}
                />
              )}
              <Box flex="1" minH={0} overflow="auto" sx={SCROLL_SX}>
                <StatusRowsTable columns={existingColumns} rows={existing} tone={isReplace ? "removed" : "kept"} />
              </Box>
              <PreviewPager page={existingPage} totalPages={existingTotalPages} onPage={setExistingPage} />
            </Flex>
          )
        ) : (
          // "after" — what the task holds once this commit lands, under one header row. Add: the rows this
          // commit writes, then the rows already there. Replace: only the new rows, because the existing
          // ones are deleted — they are named in a band rather than shown, since they will not be there.
          <Flex direction="column" h="100%">
            <Box flex="1" minH={0} overflow="auto" sx={SCROLL_SX}>
              <StatusRowsTable
                columns={afterColumns}
                rows={rows.map((r) => padTo(r, afterColumns.length))}
                tone={isReplace ? "replacing" : "added"}
                bandLabel={`+ ${mappedCount.toLocaleString()} new rows from this session`}
              />
              {isReplace ? (
                existingRowCount > 0 && (
                  <BandHeader
                    tone="removed"
                    label={`${existingRowCount.toLocaleString()} existing rows are deleted — see the Existing tab`}
                  />
                )
              ) : (
                <StatusRowsTable
                  columns={afterColumns}
                  rows={existing}
                  tone="kept"
                  bandLabel={`${(existingRowCount ?? 0).toLocaleString()} rows already in the task`}
                  hideHeader
                  isLoading={isLoadingExistingRows && !existingRows}
                />
              )}
            </Box>
            {(readyTotalPages > 1 || (!isReplace && existingTotalPages > 1)) && (
              <HStack justify="space-between" flexShrink={0} borderTop="1px solid" borderColor="gray.100" px={3} py={1.5}>
                <PagerInline label="New" page={readyPage} totalPages={readyTotalPages} onPage={setReadyPage} />
                {!isReplace && (
                  <PagerInline label="Existing" page={existingPage} totalPages={existingTotalPages} onPage={setExistingPage} />
                )}
              </HStack>
            )}
          </Flex>
        )}
      </Box>

      <PreviewFooter
        plain={plain}
        view={view}
        hasExisting={hasExisting}
        hasChangedSinceCommit={hasChangedSinceCommit}
        rowsShown={rows.length}
        existingShown={existing.length}
        totalSessionRows={totalSessionRows}
        mappedCount={mappedCount}
        existingRowCount={existingRowCount}
        isReplace={isReplace}
        canClassifyRowChanges={canClassifyRowChanges}
        onGoToTransform={onGoToTransform}
      />
    </Flex>
  );
}

const PreviewHeader = ({ plain, hasExisting, hasChangedSinceCommit, isReplace, mappedCount, view, setView, views }) => (
  <Flex align="center" justify="space-between" flexWrap="wrap" gap={3}>
    <HStack spacing={3}>
      <Flex w="30px" h="30px" borderRadius="8px" bg="gray.100" align="center" justify="center">
        <Eye size={15} color="#4A5568" />
      </Flex>
      <Text fontSize="16px" fontWeight="800" color="darkTeal">
        Preview
      </Text>
      <Box
        as="span"
        fontSize="10px"
        fontWeight="700"
        letterSpacing="0.04em"
        textTransform="uppercase"
        color="gray.500"
        bg="gray.100"
        borderRadius="full"
        px={2}
        py="2px"
      >
        Read only
      </Box>
      {!plain && <Legend hasExisting={hasExisting} hasChangedSinceCommit={hasChangedSinceCommit} isReplace={isReplace} />}
    </HStack>

    {!plain && views ? (
      <SegmentedControl views={views} active={view} onChange={setView} />
    ) : (
      <Text fontSize="13px" color="gray.600" fontWeight="600">
        {mappedCount.toLocaleString()} rows ready to commit
      </Text>
    )}
  </Flex>
);

const Legend = ({ hasExisting, hasChangedSinceCommit, isReplace }) => {
  if (hasChangedSinceCommit) {
    return (
      <HStack spacing={3} fontSize="12px" color="gray.600">
        <LegendDot color="lime" label="New" />
        <LegendDot color="gray.300" label="Committed" />
        <LegendDot color="destructive.text" label="Removed" />
      </HStack>
    );
  }
  if (!hasExisting) return null;
  return (
    <HStack spacing={3} fontSize="12px" color="gray.600">
      {isReplace ? (
        <>
          <LegendDot color="lime" label="Written" />
          <LegendDot color="destructive.text" label="Deleted" />
        </>
      ) : (
        <>
          <LegendDot color="lime" label="Added by this commit" />
          <LegendDot color="gray.300" label="Already in task" />
        </>
      )}
    </HStack>
  );
};

const LegendDot = ({ color, label }) => (
  <HStack spacing={1.5}>
    <Box w="8px" h="8px" borderRadius="full" bg={color} />
    <Text>{label}</Text>
  </HStack>
);

const SegmentedControl = ({ views, active, onChange }) => (
  <HStack spacing={0} bg="gray.100" borderRadius="full" p="3px">
    {views.map((v) => (
      <Box
        key={v.key}
        as="button"
        type="button"
        onClick={() => onChange(v.key)}
        px="12px"
        py="6px"
        borderRadius="full"
        fontSize="12.5px"
        fontWeight="700"
        whiteSpace="nowrap"
        bg={active === v.key ? "white" : "transparent"}
        color={active === v.key ? "darkTeal" : "gray.500"}
        boxShadow={active === v.key ? "0 1px 2px rgba(0,0,0,0.08)" : "none"}
        transition="all 0.12s"
      >
        {v.label}
      </Box>
    ))}
  </HStack>
);

// One tint per row status, so "kept", "added" and "deleted" read the same in every view. The 3px inset bar
// and the gutter glyph carry the meaning; the background only supports it, which keeps the table legible.
const TONES = {
  added: { bg: "#FAFDF5", bar: "lime", glyph: "+", glyphColor: "limeShade.700", bandBg: "lightLime", bandColor: "limeShade.700" },
  replacing: { bg: "lightLime", bar: "lime", glyph: "+", glyphColor: "limeShade.700", bandBg: "lightLime", bandColor: "limeShade.700" },
  kept: { bg: "white", bar: "gray.300", glyph: "", glyphColor: "gray.400", bandBg: "gray.50", bandColor: "gray.600" },
  removed: { bg: "destructive.bg", bar: "destructive.text", glyph: "−", glyphColor: "destructive.text", bandBg: "destructive.bg", bandColor: "destructive.textDark" },
};

/**
 * Rows with a status — tinted, with an inset bar and a gutter glyph — straight off the output frame or the
 * task. `DataPreviewTable` is left untouched (its own file's house rule: local, page-specific tables, see
 * frontend/CLAUDE.md) — this is a small, Commit-stage-only variant, not a prop added to the shared one.
 *
 * `bandLabel` puts a full-width label row above the rows, which is what lets the After commit view stack
 * two lists under one header; `hideHeader` is for the second of them.
 */
const StatusRowsTable = ({ columns, rows, tone, bandLabel, hideHeader = false, isLoading = false }) => {
  const t = TONES[tone];
  return (
    <Box as="table" w="100%" fontSize="xs">
      {!hideHeader && (
        <Box as="thead" position="sticky" top={0} bg="white" sx={{ zIndex: 1 }}>
          <Box as="tr">
            <Box as="th" w="40px" bg="gray.50" borderBottom="1px solid" borderColor="gray.200" />
            {columns.map((col) => (
              <Box
                as="th"
                key={col}
                textAlign="left"
                px={3}
                py={2}
                bg="gray.50"
                borderBottom="1px solid"
                borderColor="gray.200"
                whiteSpace="nowrap"
              >
                {col}
              </Box>
            ))}
          </Box>
        </Box>
      )}
      <Box as="tbody">
        {bandLabel && (
          <Box as="tr" bg={t.bandBg}>
            <Box as="td" colSpan={columns.length + 1} px={3} py={1.5} borderBottom="1px solid" borderColor="gray.100">
              <Text fontSize="11px" fontWeight="700" color={t.bandColor}>
                {bandLabel}
              </Text>
            </Box>
          </Box>
        )}
        {isLoading ? (
          <Box as="tr">
            <Box as="td" colSpan={columns.length + 1} px={3} py={3}>
              <HStack spacing={2} color="gray.500">
                <Spinner size="xs" />
                <Text fontSize="xs">Loading…</Text>
              </HStack>
            </Box>
          </Box>
        ) : (
          rows.map((row, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <Box as="tr" key={i} bg={t.bg}>
              <Box as="td" position="relative" textAlign="center" borderBottom="1px solid" borderColor="gray.100">
                <Box position="absolute" left={0} top={0} bottom={0} w="3px" bg={t.bar} />
                <Text as="span" color={t.glyphColor} fontWeight="700">
                  {t.glyph}
                </Text>
              </Box>
              {row.map((cell, j) => (
                // eslint-disable-next-line react/no-array-index-key
                <Box as="td" key={j} px={3} py={2} whiteSpace="nowrap" borderBottom="1px solid" borderColor="gray.100">
                  {formatCell(cell)}
                </Box>
              ))}
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

const BandHeader = ({ tone, label }) => {
  const t = TONES[tone];
  return (
    <HStack flexShrink={0} spacing={2} bg={t.bandBg} px={3} py={2} borderBottom="1px solid" borderColor="gray.100">
      <Box w="3px" h="14px" bg={t.bar} borderRadius="full" />
      <Text fontSize="12px" fontWeight="600" color={t.bandColor}>
        {label}
      </Text>
    </HStack>
  );
};

// Prev / "Page p of P" / next. Hidden for a single page, like WorkingPanel's own pager.
const PreviewPager = ({ page, totalPages, onPage }) => {
  if (!totalPages || totalPages <= 1) return null;
  return (
    <HStack flexShrink={0} justify="center" borderTop="1px solid" borderColor="gray.100" py={1.5}>
      <PagerInline page={page} totalPages={totalPages} onPage={onPage} />
    </HStack>
  );
};

const PagerInline = ({ label, page, totalPages, onPage }) => {
  if (!totalPages || totalPages <= 1) return <Box />;
  return (
    <HStack spacing={2} fontSize="12px" color="gray.600">
      {label && <Text fontWeight="600">{label}</Text>}
      <IconButton
        aria-label={`Previous page${label ? ` of ${label.toLowerCase()} rows` : ""}`}
        icon={<CaretLeft size={12} weight="bold" />}
        size="xs"
        variant="outline"
        borderRadius="full"
        borderColor="gray.300"
        isDisabled={page <= 1}
        onClick={() => onPage(page - 1)}
      />
      <Text>
        Page{" "}
        <Text as="span" fontWeight="700" color="gray.800">
          {page}
        </Text>{" "}
        of{" "}
        <Text as="span" fontWeight="700" color="gray.800">
          {totalPages}
        </Text>
      </Text>
      <IconButton
        aria-label={`Next page${label ? ` of ${label.toLowerCase()} rows` : ""}`}
        icon={<CaretRight size={12} weight="bold" />}
        size="xs"
        variant="outline"
        borderRadius="full"
        borderColor="gray.300"
        isDisabled={page >= totalPages}
        onClick={() => onPage(page + 1)}
      />
    </HStack>
  );
};

const LoadingNotice = ({ label }) => (
  <Flex align="center" justify="center" h="100%" color="gray.500" gap={2}>
    <Spinner size="sm" />
    <Text fontSize="13px">{label}</Text>
  </Flex>
);

const EmptyExisting = ({ isLoading }) => (
  <Flex align="center" justify="center" h="100%" direction="column" gap={2} color="gray.500" px={6} textAlign="center">
    <Text fontSize="13px" fontWeight="600" color="gray.600">
      {isLoading ? "…" : "This part of the task has no rows yet"}
    </Text>
    <Text fontSize="12px" color="gray.400" maxW="40ch">
      Committing adds the mapped rows; there is nothing to keep or replace.
    </Text>
  </Flex>
);

const ChangesUnavailableNotice = () => (
  <Flex align="center" justify="center" h="100%" direction="column" gap={2} color="gray.500" px={6} textAlign="center">
    <Text fontSize="13px" fontWeight="600" color="gray.600">
      Row-level change tracking isn&apos;t available yet
    </Text>
    <Text fontSize="12px" color="gray.400" maxW="42ch">
      Switch to &quot;All session rows&quot; to see everything in this session.
    </Text>
  </Flex>
);

const AllCommittedBanner = ({ lastCommit, mappedCount }) => (
  <Flex
    align="center"
    justify="space-between"
    flexWrap="wrap"
    gap={3}
    bg="successCard.bg"
    border="1px solid"
    borderColor="successCard.border"
    borderRadius="12px"
    px={4}
    py={3}
  >
    <HStack spacing={3} align="start">
      <Flex w="32px" h="32px" flexShrink={0} borderRadius="8px" bg="lime" align="center" justify="center">
        <FlagCheckered size={16} weight="fill" color="#051F23" />
      </Flex>
      <Box>
        <Text fontSize="14px" fontWeight="800" color="darkTeal">
          All data committed
        </Text>
        <Text fontSize="12.5px" color="gray.600">
          All {mappedCount.toLocaleString()} rows in this session have been written to the task. Nothing is
          waiting to commit.
        </Text>
      </Box>
    </HStack>
    <Text fontSize="12px" color="gray.500" textAlign="right">
      Committed {formatCommittedAt(lastCommit.at)}
      <br />
      by you
    </Text>
  </Flex>
);

function formatCommittedAt(timestamp) {
  if (!timestamp) return "";
  return new Date(timestamp).toLocaleString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const PreviewFooter = ({
  plain,
  view,
  hasExisting,
  hasChangedSinceCommit,
  rowsShown,
  existingShown,
  totalSessionRows,
  mappedCount,
  existingRowCount,
  isReplace,
  canClassifyRowChanges,
  onGoToTransform,
}) => {
  const countText = (() => {
    if (plain || view === "ready" || view === "allSession") {
      return `Showing ${rowsShown} of ${totalSessionRows.toLocaleString()} rows`;
    }
    if (view === "existing") {
      return `Showing ${existingShown.toLocaleString()} of ${(existingRowCount ?? 0).toLocaleString()} existing rows`;
    }
    if (view === "changes") {
      return canClassifyRowChanges
        ? "New and removed rows since your last commit"
        : "Row-level change tracking isn't available yet";
    }
    // "after" — brief §2: Replace reads "N written · M deleted"; Add reads the usual row-count sentence.
    if (isReplace) {
      return `${mappedCount.toLocaleString()} written · ${(existingRowCount ?? 0).toLocaleString()} deleted`;
    }
    const after = (existingRowCount ?? 0) + mappedCount;
    return `${mappedCount.toLocaleString()} new + ${(existingRowCount ?? 0).toLocaleString()} existing = ${after.toLocaleString()} rows after commit`;
  })();

  return (
    <Flex align="center" justify="space-between" flexWrap="wrap" gap={3}>
      <Text fontSize="12px" color="gray.500">
        {countText}
      </Text>
      {onGoToTransform && (
        <Box
          as="button"
          type="button"
          onClick={onGoToTransform}
          display="inline-flex"
          alignItems="center"
          gap={2}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="full"
          boxShadow="0 2px 6px rgba(0,0,0,0.08)"
          px={3}
          py="6px"
        >
          <Flex w="20px" h="20px" borderRadius="full" bg="gray.100" align="center" justify="center">
            <PencilSimple size={11} color="#4A5568" />
          </Flex>
          <Text fontSize="12px" color="gray.600">
            Return to{" "}
            <Text as="span" fontWeight="700" color="limeShade.700">
              Transform
            </Text>{" "}
            to edit this data
          </Text>
        </Box>
      )}
    </Flex>
  );
};

export default CommitPreview;
